<script setup lang="ts">
import type { AutoAdjustment } from '~/composables/useCheckoutConfigurator'

/**
 * What the checkout changed on the customer's behalf.
 *
 * The one case today is the main disk being raised to an operating system's
 * minimum, which used to happen in silence: the disk grew, the price in the
 * sidebar moved with it, and the only explanation was a line in a step the
 * customer had already scrolled past — and no line at all when the source was
 * one of their own images, because the note needed a name it was never given.
 *
 * Three deliberate choices here:
 *
 * - `role="status"` (a polite live region), because this is information, not an
 *   error. Every change on this page is otherwise a purely visual event, and a
 *   price that moves without saying so is the same defect for a screen reader
 *   user as the silent disk raise was for everyone.
 * - It is the SAME sentence everybody gets. A separate announcement built for
 *   assistive tech is one that drifts from what is on screen.
 * - Persistent, not a toast. A toast is missable, and this moved the price.
 */
defineProps<{
  items: AutoAdjustment[]
}>()
</script>

<template>
  <div
    role="status"
    aria-live="polite"
    aria-atomic="true"
    class="empty:hidden"
  >
    <div
      v-if="items.length > 0"
      class="rounded-lg border border-info/30 bg-info/5 p-3"
    >
      <div class="flex items-start gap-2">
        <UIcon
          name="i-lucide-info"
          class="mt-0.5 size-4 shrink-0 text-info"
          aria-hidden="true"
        />
        <div class="min-w-0 space-y-1">
          <p class="text-xs font-medium">
            {{ $t('billing.page.checkout.autoAdjust.title') }}
          </p>
          <p
            v-for="item in items"
            :key="item.key"
            class="text-xs leading-relaxed text-toned"
          >
            {{ item.label }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
